import React from "react"

import { cn } from "@/utils/cn"

import {
  DISTRIBUTION_STAT_KEY,
  type DistributionStatKey,
  type DistributionStats,
} from "./distributionStatsUtils"

interface HistogramDistributionStatsProps {
  stats: DistributionStats
  formatValue: (v: number) => string
  units?: string
  /** When set, stats are clickable to toggle reference lines on the chart. */
  visibleStats?: Set<DistributionStatKey>
  onToggleStat?: (key: DistributionStatKey) => void
  /** When set, hovering a stat shows its reference line on the chart. */
  onHoverStat?: (key: DistributionStatKey | null) => void
}

const DISTRIBUTION_COLUMNS: Array<{
  key: DistributionStatKey
  label: string
}> = [
  { key: DISTRIBUTION_STAT_KEY.MEAN, label: "Mean" },
  { key: DISTRIBUTION_STAT_KEY.STD_DEV, label: "Standard Deviation" },
  { key: DISTRIBUTION_STAT_KEY.MEDIAN, label: "Median" },
  { key: DISTRIBUTION_STAT_KEY.P95, label: "P95" },
]

/**
 * Displays distribution stats in 4 columns (name + number in h1)
 * for histogram metric detail charts. When onToggleStat is provided,
 * clicking a stat toggles its reference line on the chart.
 */
export const HistogramDistributionStats: React.FC<
  HistogramDistributionStatsProps
> = ({
  stats,
  formatValue,
  units,
  visibleStats,
  onToggleStat,
  onHoverStat,
}) => {
  const unitSuffix = units ? ` ${units}` : ""
  const isInteractive = onToggleStat != null

  return (
    <div className="grid grid-cols-4 gap-4 text-coval-text-muted">
      {DISTRIBUTION_COLUMNS.map(({ key, label }) => {
        const isActive = visibleStats?.has(key) ?? false
        const content = (
          <>
            <span className="text-sm font-medium text-foreground">{label}</span>
            <h1 className="text-2xl font-bold text-foreground mt-0.5">
              {formatValue(stats[key])}
              {unitSuffix}
            </h1>
          </>
        )
        if (isInteractive) {
          return (
            <button
              key={key}
              type="button"
              aria-label={`Toggle ${label} reference line`}
              aria-pressed={isActive}
              onClick={() => onToggleStat(key)}
              onMouseEnter={() => onHoverStat?.(key)}
              onMouseLeave={() => onHoverStat?.(null)}
              className={cn(
                "flex flex-col rounded px-3 py-2 text-left transition-colors border border-coval-border",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                "hover:bg-surface-1-hover",
                isActive ? "bg-surface-1" : "bg-background",
              )}
            >
              {content}
            </button>
          )
        }
        return (
          <div
            key={key}
            className="flex flex-col rounded px-3 py-2 border border-coval-border bg-background"
          >
            {content}
          </div>
        )
      })}
    </div>
  )
}
